import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Palette, Globe, Video, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VideoSlider from "@/components/VideoSlider";
import ServiceCard from "@/components/ServiceCard";
import ScrollReveal from "@/components/ScrollReveal";
import ClientLogos from "@/components/ClientLogos";
import SEO from "@/components/SEO";

const services = [
  {
    icon: Palette,
    title: "Corporate Identity",
    description: "Logo-Design, Branding & komplette Geschäftsausstattung. Wir schaffen Marken, die im Gedächtnis bleiben.",
    link: "/leistungen",
  },
  {
    icon: Globe,
    title: "Webseiten-Erstellung",
    description: "Responsive Webseiten mit modernem Design, CMS-Integration und Performance-Optimierung.",
    link: "/leistungen",
  },
  {
    icon: Video,
    title: "Medienproduktion",
    description: "Von Motorsport-Highlights bis Event-Aftermovies. Videoproduktion & Fotografie auf höchstem Niveau, deutschlandweit.",
    link: "/leistungen",
  },
  {
    icon: Share2,
    title: "Social Media",
    description: "Strategische Betreuung, kreative Content-Erstellung & datengetriebenes Marketing für maximale Reichweite. Social Media Agentur aus Bochum.",
    link: "/leistungen",
  },
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO title="4Facemedia – Kreativagentur Bochum | Videoproduktion & Webdesign deutschlandweit" description="4Facemedia ist deine 360° Kreativagentur aus Bochum. Corporate Identity, Webdesign, Videoproduktion & Social Media Marketing deutschlandweit. Jetzt Projekt starten!" path="/" />
      <Header />

      {/* Cinematic Hero — fullscreen, immersive */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-[15%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" style={{ animation: "float 10s ease-in-out infinite" }} />
          <div className="absolute bottom-1/4 left-[5%] w-[600px] h-[600px] rounded-full bg-glow/4 blur-[150px]" style={{ animation: "float 12s ease-in-out infinite 3s" }} />
        </div>

        {/* Decorative lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-[20%] w-[1px] h-full bg-gradient-to-b from-transparent via-foreground/5 to-transparent" />
          <div className="absolute top-0 left-[80%] w-[1px] h-full bg-gradient-to-b from-transparent via-foreground/5 to-transparent" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <div className="overflow-hidden mb-8">
              <span className="inline-block text-sm tracking-[0.3em] uppercase text-primary/80 font-medium animate-fade-up">
                360° Kreativagentur aus Bochum
              </span>
            </div>
            <div className="overflow-hidden">
              <h1 className="text-5xl md:text-7xl lg:text-[6.5rem] font-bold leading-[0.95] mb-8 animate-fade-up-delay-1">
                <span className="text-foreground block">Wir machen</span>
                <span className="text-gradient block">deine Marke</span>
                <span className="text-foreground block">unvergesslich.</span>
              </h1>
            </div>
            <p className="text-lg md:text-xl text-foreground/85 max-w-2xl mb-12 leading-relaxed font-light animate-fade-up-delay-2">
              Von der Corporate Identity über Webdesign und Videoproduktion bis hin zu Social Media Marketing. Aus Bochum, deutschlandweit für dich im Einsatz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up-delay-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground text-base px-10 h-14 rounded-full">
                <Link to="/kontakt">
                  Projekt starten <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-foreground/20 hover:bg-foreground/5 text-foreground text-base px-10 h-14 rounded-full">
                <Link to="/projekte">Projekte ansehen</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 animate-fade-up-delay-3">
          <span className="text-[10px] tracking-[0.3em] uppercase text-foreground/30">Scroll</span>
          <ArrowDown size={16} className="text-foreground/30 animate-bounce" />
        </div>
      </section>

      {/* Video Slider — fullscreen cinematic */}
      <VideoSlider />

      {/* Services Overview */}
      <section className="py-32 relative">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="max-w-3xl mb-20">
              <span className="text-xs tracking-[0.3em] uppercase text-primary/80 font-medium">Unser Angebot</span>
              <h2 className="text-4xl md:text-6xl font-bold text-foreground mt-6 leading-tight">
                Das 360° Modell
              </h2>
              <p className="text-foreground/85 mt-6 text-lg font-light leading-relaxed max-w-xl">
                Alles aus einer Hand. Ob Bochum, NRW oder bundesweit: Markenentwicklung, Webdesign, Videoproduktion & Social Media.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <ScrollReveal key={service.title} delay={i * 100}>
                <ServiceCard {...service} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-32 relative">
        <div className="absolute inset-0 bg-card/20" />
        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <ScrollReveal>
              <div>
                <span className="text-xs tracking-[0.3em] uppercase text-primary/80 font-medium">Über uns</span>
                <h2 className="text-4xl md:text-6xl font-bold text-foreground mt-6 mb-8 leading-tight">
                  Kreativität trifft<br />
                  <span className="text-gradient">Strategie.</span>
                </h2>
                <p className="text-foreground/85 leading-relaxed mb-6 font-light text-lg">
                  Bei 4FACEMEDIA vereinen wir kreatives Handwerk mit strategischem Denken. Unser Team aus Designern, Entwicklern, Filmemachern & Marketing-Experten arbeitet Hand in Hand, mit Sitz in Bochum und Projekten in ganz Deutschland.
                </p>
                <p className="text-foreground/85 leading-relaxed mb-10 font-light text-lg">
                  Ob Motorsport-Highlight in Italien, Produktfotografie im Ruhrgebiet oder Social-Media-Kampagne für bundesweite Marken: wir liefern Ergebnisse, die begeistern.
                </p>
                <Link
                  to="/ueber-uns"
                  className="inline-flex items-center gap-3 text-foreground text-sm font-medium tracking-wide group"
                >
                  Mehr über uns
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { number: "70+", label: "Projekte" },
                  { number: "50+", label: "Kunden" },
                  { number: "3+", label: "Jahre Erfahrung" },
                  { number: "360°", label: "Service" },
                ].map((stat) => (
                  <div key={stat.label} className="p-10 rounded-2xl bg-gradient-card border border-border/30 text-center group hover:border-primary/20 transition-all duration-500 hover:scale-105">
                    <div className="text-4xl font-bold text-gradient mb-3">{stat.number}</div>
                    <div className="text-sm text-foreground/85 font-light">{stat.label}</div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA — cinematic */}
      <section className="py-40 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/5 blur-[200px]" />
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-8 leading-tight">
              Bereit für dein<br />
              <span className="text-gradient">nächstes Projekt?</span>
            </h2>
            <p className="text-foreground/85 text-lg max-w-xl mx-auto mb-12 font-light">
              Lass uns gemeinsam etwas Großartiges erschaffen.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground text-base px-12 h-14 rounded-full">
              <Link to="/kontakt">
                Jetzt Kontakt aufnehmen <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      <ClientLogos />
      <Footer />
    </div>
  );
};

export default Index;
